import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { updateSubTaskStatus } from '../../../Redux/actions';
import { buttonStyles } from '../../../theme/formStyles';
import { subTaskStatusStyle } from '../projectSchedule.utils';

// Cancelled is left to the project edit form: a cancelled sub-task drops off every calendar
const STATUSES = ['Pending', 'In Progress', 'On Hold', 'Completed'];

// Segmented status switch for one sub-task. Admins and the sub-task's SPOC can change it (checked again
// by the server); everyone else sees it read-only. Completing asks for confirmation first.
export default function SubTaskStatusControl({ task, canChange, onChanged }) {
    const dispatch = useDispatch();
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(null);
    const [error, setError] = useState(null);

    const save = (status) => {
        setSaving(status); setError(null);
        dispatch(updateSubTaskStatus(task.id, status)).then((res) => {
            setSaving(null);
            setConfirming(false);
            if (res?.data?.success) onChanged();
            else setError(res?.data?.message || 'Could not change the status');
        });
    };

    const pick = (status) => {
        if (!canChange || status === task.status || saving) return;
        if (status === 'Completed') setConfirming(true);
        else save(status);
    };

    return (
        <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', border: '1px solid #e5e9ee', borderRadius: 3, overflow: 'hidden' }}>
                {STATUSES.map((s, i) => {
                    const on = task.status === s;
                    const look = subTaskStatusStyle(s);
                    return (
                        <ButtonBase key={s} onClick={() => pick(s)} disabled={!canChange && !on}
                            sx={{
                                py: 1.1, px: 0.5, fontSize: '12.5px', fontWeight: on ? 700 : 500, lineHeight: 1.2,
                                borderLeft: i ? '1px solid #e5e9ee' : 'none',
                                color: on ? (s === 'Completed' ? '#fff' : look.color) : canChange ? '#6b7785' : '#b0b8c1',
                                bgcolor: on ? (s === 'Completed' ? '#16a34a' : look.bgcolor) : '#fff',
                                cursor: canChange && !on ? 'pointer' : 'default',
                                '&:hover': canChange && !on ? { bgcolor: '#f7fafc' } : undefined,
                            }}>
                            {saving === s ? <CircularProgress size={14} sx={{ color: look.color }} /> : <>{s === 'Completed' && on ? '✓ ' : ''}{s}</>}
                        </ButtonBase>
                    );
                })}
            </Box>
            {!canChange && (
                <Typography sx={{ fontSize: '12px', color: '#6b7785', mt: 0.75 }}>Only an admin or this sub-task's SPOC can change the status.</Typography>
            )}
            {confirming && (
                <Box sx={{ mt: 1.25, bgcolor: '#fffbea', border: '1px solid #f5e3a3', borderRadius: 2.5, p: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '13.5px' }}>Mark as completed?</Typography>
                    <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }}>Project progress and the timeline will count this sub-task as done.</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
                        <Button size="small" variant="contained" sx={buttonStyles.contained} disabled={!!saving} onClick={() => save('Completed')}>
                            {saving ? <CircularProgress size={16} color="inherit" /> : 'Yes, completed'}
                        </Button>
                        <Button size="small" variant="outlined" sx={buttonStyles.outlined} disabled={!!saving} onClick={() => setConfirming(false)}>Cancel</Button>
                    </Box>
                </Box>
            )}
            {error && <Alert severity="error" sx={{ mt: 1.25 }} onClose={() => setError(null)}>{error}</Alert>}
        </Box>
    );
}
